/** "Datum wählen" month calendar (PLA-880, prototype `.h-cal*`). Presentational. */
import { FC } from "react"
import { Pressable, PressableStateCallbackType, TextStyle, View, ViewStyle } from "react-native"

import { Text } from "@/components/Text"

import { NavArrow } from "./NavArrow"
import {
  WEEKDAY_HEADERS,
  type CalendarCell,
  type CalendarMonthView,
} from "./rescheduleCalendarModel"
import { font, tabular, tokens } from "./tokens"

export interface CalendarViewProps {
  view: CalendarMonthView
  onPrevMonth: () => void
  onNextMonth: () => void
  onPick: (date: string) => void
}

type HoverState = PressableStateCallbackType & { hovered?: boolean }

export const CalendarView: FC<CalendarViewProps> = ({ view, onPrevMonth, onNextMonth, onPick }) => (
  <View style={$wrap}>
    <View style={$head}>
      <NavArrow
        direction="left"
        label="Vorheriger Monat"
        disabled={!view.canPrev}
        onPress={onPrevMonth}
        testID="cal-prev"
      />
      <Text style={$title} testID="cal-title">
        {view.title}
      </Text>
      <NavArrow
        direction="right"
        label="Nächster Monat"
        disabled={!view.canNext}
        onPress={onNextMonth}
        testID="cal-next"
      />
    </View>
    <View style={$grid}>
      <View style={$row}>
        <View style={$kwCol} />
        {WEEKDAY_HEADERS.map((d) => (
          <Text key={d} style={$weekday}>
            {d}
          </Text>
        ))}
      </View>
      {view.weeks.map((week) => (
        <View key={week.cells.find(Boolean)?.date} style={$row}>
          <Text style={$kw} numberOfLines={1} testID={`cal-kw-${week.kw}`}>{`KW ${week.kw}`}</Text>
          {week.cells.map((cell, i) =>
            cell ? (
              <Cell key={cell.date} cell={cell} onPick={onPick} />
            ) : (
              <View key={`blank-${i}`} style={$cell} />
            ),
          )}
        </View>
      ))}
    </View>
    {view.showLegend && (
      <View style={$legend}>
        <Text style={$legendKey}>12</Text>
        <Text style={$legendText}>= freie Uhrzeiten an diesem Tag</Text>
      </View>
    )}
  </View>
)

const Cell: FC<{ cell: CalendarCell; onPick: (date: string) => void }> = ({ cell, onPick }) => (
  <Pressable
    accessibilityRole="button"
    accessibilityLabel={
      cell.count !== null
        ? `${cell.day}. ${cell.date.slice(5, 7)}.: ${cell.count} freie Uhrzeiten`
        : `${cell.day}. ${cell.date.slice(5, 7)}.`
    }
    accessibilityState={{ disabled: !cell.open }}
    disabled={!cell.open}
    onPress={() => onPick(cell.date)}
    testID={`cal-cell-${cell.date}`}
    style={(state) => [
      $cell,
      cell.open ? $open : $off,
      cell.open && (state as HoverState).hovered && $hover,
    ]}
  >
    <Text style={[$num, !cell.open && $numOff]}>{cell.day}</Text>
    {cell.open && cell.count !== null && (
      <Text style={$tag} testID={`cal-count-${cell.date}`}>
        {cell.count}
      </Text>
    )}
  </Pressable>
)

const $wrap: ViewStyle = { gap: 16 }
const $head: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
}
const $title: TextStyle = { fontSize: 17, fontFamily: font.bold, color: tokens.ink }
const $grid: ViewStyle = { gap: 4 }
const $row: ViewStyle = { flexDirection: "row", alignItems: "center", gap: 4 }
const $kwCol: ViewStyle = { width: 34 }
const $kw: TextStyle = { width: 34, fontSize: 11, fontFamily: font.semi, color: tokens.ink70 }
const $weekday: TextStyle = {
  flex: 1,
  minWidth: 0,
  textAlign: "center",
  fontSize: 12,
  paddingBottom: 2,
  fontFamily: font.bold,
  color: tokens.ink70,
}
const $cell: ViewStyle = {
  flex: 1,
  minWidth: 0,
  height: 48,
  borderRadius: 12,
  alignItems: "center",
  justifyContent: "center",
  gap: 3,
}
const $open: ViewStyle = { backgroundColor: tokens.mist }
const $off: ViewStyle = { backgroundColor: "transparent" }
const $hover: ViewStyle = { borderWidth: 1, borderColor: tokens.field }
const $num: TextStyle = {
  fontSize: 18,
  lineHeight: 18,
  fontFamily: font.bold,
  color: tokens.ink,
  ...tabular,
}
const $numOff: TextStyle = {
  fontSize: 16,
  lineHeight: 18,
  fontFamily: font.medium,
  color: tokens.ink32,
}
const $tag: TextStyle = { fontSize: 11, lineHeight: 11, fontFamily: font.bold, color: tokens.ok }
const $legend: ViewStyle = { flexDirection: "row", alignItems: "center", gap: 6 }
const $legendKey: TextStyle = { fontSize: 12, fontFamily: font.bold, color: tokens.ok }
const $legendText: TextStyle = { fontSize: 12, fontFamily: font.regular, color: tokens.ink70 }
